import { gql, ApolloClient, NormalizedCacheObject, FetchResult } from '@apollo/client';
import { logger } from './logger';
import {
    USER_ADDED,
    USER_DELETED,
    STATION_ADDED,
    ROUTE_ADDED,
    ROUTE_UPDATED,
    ROUTE_DELETED,
    ROUTE_TEMPLATE_ADDED,
    ROUTE_TEMPLATE_UPDATED,
    ROUTE_TEMPLATE_DELETED
} from '../graphql/subscriptions';
import type { Subscription } from 'zen-observable-ts';
import { Station } from '../views/stations/types';
import type { Reference } from '@apollo/client';

export const prepareSubscriptions = (
    client: ApolloClient<NormalizedCacheObject>,
    tenantId: string
): Record<string, () => Promise<Subscription>> => ({
    STATION_ADDED: async () => {
        const observable = client.subscribe({
            query: STATION_ADDED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const station = data?.stationAdded;
                //console.log('Station added:', data);
                if (!station) return;

                client.cache.modify({
                    fields: {
                        listStations(existingRefs = []) {
                            const newRef = client.cache.writeFragment({
                                data: station,
                                fragment: gql`
                                    fragment NewStation on Station {
                                        id
                                        type
                                        name
                                        remotePortalId
                                    }
                                `
                            });

                            if (existingRefs.some((ref: Reference) => ref?.__ref === newRef?.__ref)) {
                                return existingRefs;
                            }
                            //console.log('Adding new station to cache:', newRef);
                            return [...existingRefs, newRef];
                        }
                    }
                });
            },
            error: (error) => {
                console.warn('[STATION_ADDED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    USER_ADDED: async () => {
        const observable = client.subscribe({
            query: USER_ADDED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const user = data?.userAdded;
                if (!user) return;
                //console.log('User added:', user);
                client.cache.modify({
                    fields: {
                        listUsers(existingRefs = []) {
                            const newRef = client.cache.writeFragment({
                                data: user,
                                fragment: gql`
                                    fragment NewUser on User {
                                        id
                                        email
                                        enabled
                                        group
                                        created
                                        modified
                                    }
                                `
                            });

                            if (existingRefs.some((ref: Reference) => ref?.__ref === newRef?.__ref)) {
                                return existingRefs;
                            }

                            return [...existingRefs, newRef];
                        }
                    }
                });
            },
            error: (error) => {
                console.warn('[USER_ADDED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    USER_DELETED: async () => {
        const observable = client.subscribe({
            query: USER_DELETED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const user = data?.userDeleted;
                if (!user?.id) return;

                const deleteRef = client.cache.identify({ id: user.id, __typename: 'User' });

                client.cache.modify({
                    fields: {
                        listUsers(existing: readonly Reference[] = []) {
                            return existing.filter((ref) => ref.__ref !== deleteRef);
                        }
                    }
                });

                client.cache.evict({ id: deleteRef });
                client.cache.gc();
            },
            error: (error) => {
                console.warn('[USER_DELETED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    ROUTE_ADDED: async () => {
        const observable = client.subscribe({
            query: ROUTE_ADDED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const route = data?.routeAdded;
                logger.log('[ROUTE_ADDED] Subscription fired:', data);
                if (!route) return;

                client.cache.modify({
                    fields: {
                        listRoutes(existingRefs = []) {
                            const newRef = client.cache.writeFragment({
                                data: route,
                                fragment: gql`
                                    fragment NewRoute on Route {
                                        id
                                        name
                                        status
                                        track
                                        __typename
                                        totalStops
                                        actionLog {
                                            id
                                            action
                                            timestamp
                                            userId
                                            userEmail
                                            stop {
                                                id
                                                name
                                            }
                                        }
                                        stops {
                                            id
                                            name
                                            stationId
                                            __typename
                                            type
                                            status
                                            asynchronousChildren
                                        }
                                    }
                                `
                            });

                            if (existingRefs.some((ref: Reference) => ref?.__ref === newRef?.__ref)) {
                                logger.log('[ROUTE_ADDED] Route already exists in cache, skipping');
                                return existingRefs;
                            }

                            logger.log('[ROUTE_ADDED] Adding new route to cache:', newRef);
                            return [...existingRefs, newRef];
                        }
                    }
                });
            },
            error: (error) => {
                console.warn('[ROUTE_ADDED] Subscription error:', error.errors[0].message || error);
            }
        });

        return subscription;
    },

    ROUTE_UPDATED: async () => {
        const observable = client.subscribe({
            query: ROUTE_UPDATED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const route = data?.routeUpdated;
                if (!route) return;

                // Write the updated route directly to cache
                client.cache.writeFragment({
                    data: route,
                    fragment: gql`
                        fragment UpdatedRoute on Route {
                            id
                            name
                            status
                            track
                            __typename
                            totalStops
                            actionLog {
                                id
                                action
                                timestamp
                                userId
                                userEmail
                                stop {
                                    id
                                    name
                                }
                            }
                            stops {
                                id
                                name
                                stationId
                                __typename
                                type
                                status
                                asynchronousChildren
                            }
                        }
                    `
                });
            },
            error: (error) => {
                console.warn('[ROUTE_UPDATED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    ROUTE_DELETED: async () => {
        const observable = client.subscribe({
            query: ROUTE_DELETED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const route = data?.routeDeleted;
                if (!route?.id) return;

                const deleteRef = client.cache.identify({ id: route.id, __typename: 'Route' });

                client.cache.modify({
                    fields: {
                        listRoutes(existing: readonly Reference[] = []) {
                            return existing.filter((ref) => ref.__ref !== deleteRef);
                        }
                    }
                });

                client.cache.evict({ id: deleteRef });
                client.cache.gc();
            },
            error: (error) => {
                console.warn('[ROUTE_DELETED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    ROUTE_TEMPLATE_ADDED: async () => {
        const observable = client.subscribe({
            query: ROUTE_TEMPLATE_ADDED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const template = data?.routeTemplateAdded;
                if (!template) return;

                client.cache.modify({
                    fields: {
                        listRouteTemplates(existingRefs = []) {
                            const newRef = client.cache.writeFragment({
                                data: template,
                                fragment: gql`
                                    fragment NewRouteTemplate on RouteTemplate {
                                        id
                                        name
                                        stops {
                                            id
                                            name
                                            type
                                            stationId
                                            asynchronousChildren
                                        }
                                        track
                                        description
                                    }
                                `
                            });

                            if (existingRefs.some((ref: Reference) => ref?.__ref === newRef?.__ref)) {
                                return existingRefs;
                            }

                            return [...existingRefs, newRef];
                        }
                    }
                });
            },
            error: (error) => {
                console.warn('[ROUTE_TEMPLATE_ADDED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    ROUTE_TEMPLATE_UPDATED: async () => {
        const observable = client.subscribe({
            query: ROUTE_TEMPLATE_UPDATED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const template = data?.routeTemplateUpdated;
                if (!template) return;

                // Write the updated template directly to cache
                client.cache.writeFragment({
                    data: template,
                    fragment: gql`
                        fragment UpdatedRouteTemplate on RouteTemplate {
                            id
                            name
                            stops {
                                id
                                name
                                type
                                stationId
                                asynchronousChildren
                            }
                            track
                            description
                        }
                    `
                });
            },
            error: (error) => {
                console.warn('[ROUTE_TEMPLATE_UPDATED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    },

    ROUTE_TEMPLATE_DELETED: async () => {
        const observable = client.subscribe({
            query: ROUTE_TEMPLATE_DELETED,
            variables: { tenantId }
        });

        const subscription = observable.subscribe({
            next: ({ data }) => {
                const template = data?.routeTemplateDeleted;
                if (!template?.id) return;

                const deleteRef = client.cache.identify({ id: template.id, __typename: 'RouteTemplate' });

                client.cache.modify({
                    fields: {
                        listRouteTemplates(existing: readonly Reference[] = []) {
                            return existing.filter((ref) => ref.__ref !== deleteRef);
                        }
                    }
                });

                client.cache.evict({ id: deleteRef });
                client.cache.gc();
            },
            error: (error) => {
                console.warn('[ROUTE_TEMPLATE_DELETED] Subscription error:', error.message || error);
            }
        });

        return subscription;
    }
});
